import { SectionSchedule } from '../../../types/sectionMaintainerTypes';
import { Currency } from '../../../types/tariff';

/**
 * Tipo de programa de educación continua (del CeStudyPlan)
 */
export enum ContinuousEducationType {
  COURSE = 'course',
  PROGRAM_COURSE = 'program_course',
}

/**
 * Estado de la oferta de educación continua
 */
export enum CeAcademicOfferStatus {
  Borrador = 'Borrador',
  Activa = 'Activa',
  Cerrada = 'Cerrada',
}

/**
 * Interfaz para TuitionProcessType
 */
export interface TuitionProcessType {
  id: number;
  name: string;
  code: string;
}

export type AcademicOfferRequirement = {
  ceAdmissionRequirementId: string;
  description: string;
  requiresDocument: boolean;
};

export type CeAcademicOfferAdmissionRequirement = {
  id: number;
  ceAcademicOfferId: number;
  ceAdmissionRequirementId: string;
  description: string;
  requiresDocument: boolean;
  isActive: boolean;
  ceAdmissionRequirement: {
    id: string;
    code: string;
    description: string;
    requiresDocument: boolean;
  };
};

export type AcademicOfferFinantialConfig = {
  code: string;
  value: number;
};

/**
 * Interfaz para TuitionProcess
 */
export interface TuitionProcess {
  id: number;
  name: string;
  tuitionProcessType?: TuitionProcessType;
}

/**
 * Interfaz para TuitionSubprocess
 */
export interface TuitionSubprocess {
  id: number;
  type: string;
  process?: TuitionProcess;
}

/**
 * Interfaz para StudyPlanVersionOfferPeriodAdmission
 */
export interface StudyPlanVersionOfferPeriodAdmission {
  id: number;
  quota: number | null;
  subprocess?: TuitionSubprocess;
}

export interface SectionPackages {
  id: number;
  sections: AcademicOfferScheduleSection[];
}

/**
 * Interfaz para Modality
 */
export interface Modality {
  id: number;
  name: string;
  code: string;
  deliveryMode: string;
}

/**
 * Interfaz para StudyPlan (plan de estudio de EC)
 */
export interface StudyPlan {
  id: number;
  name: string;
  code: string;
  school?: { id: number; name: string };
  modality?: Modality;
  studyPlanCe?: {
    id: number;
    type: ContinuousEducationType;
  };
}

/**
 * Interfaz para StudyPlanVersionOffer
 */
export interface StudyPlanVersionOffer {
  id: number;
  campusId: number;
  scheduleId: number;
  campus?: {
    id: number;
    name: string;
  };
  schedule?: {
    id: number;
    name: string;
  };
  studyPlanVersion?: {
    id: number;
    name: string;
    studyPlan?: StudyPlan;
  };
}

/**
 * Interfaz para AcademicOfferStudyPlanVersionOffer (relación intermedia)
 */
export interface AcademicOfferStudyPlanVersionOffer {
  id: number;
  quota: number | null;
  completionDaysLimit: number | null;
  duration: string | null;
  startDate?: string | null;
  endDate?: string | null;
  studyPlanVersionOffer?: StudyPlanVersionOffer;
  admissions?: StudyPlanVersionOfferPeriodAdmission[];
  sectionPackages?: SectionPackages[];
}

/**
 * Interfaz para la oferta de educación continua (CeAcademicOffer)
 */
export interface CeAcademicOffer {
  id: number;
  status: CeAcademicOfferStatus;
  firstInstallmentRuleId: number | null;
  createdAt: string;
  updatedAt: string;
  enrolled?: number;
  enrollmentStartDate?: string | null;
  enrollmentEndDate?: string | null;
  year?: number;
  offerLink?: string;
  noEnrollmentRequired?: boolean;
  tuitionProcessTypeId?: number;
  academicOfferStudyPlanVersionOffers?: AcademicOfferStudyPlanVersionOffer[];
  ceAcademicOfferAdmissionRequirements?: CeAcademicOfferAdmissionRequirement[];
  requirements?: AcademicOfferRequirement[];
  finantialConfigs?: AcademicOfferFinantialConfig[];
}

/**
 * Datos del formulario para crear/editar oferta
 */
export interface CeAcademicOfferFormData {
  campuses: Array<{ id: number; name: string }>;
  studyPlans: Array<StudyPlan>;
  modalities: Array<Modality>;
  admissionRequirements: Array<{ id: string; name: string }>;
  processTypes: Array<TuitionProcessType>;
  defaultCurrency: Currency;
}

/**
 * Filtros para la búsqueda de ofertas
 */
export interface CeAcademicOfferFilters {
  processTypeId?: number;
  programType?: ContinuousEducationType;
  studyPlanId?: number;
  modalityId?: number;
  campusId?: number;
  status?: CeAcademicOfferStatus;
  page?: number;
  items_per_page?: number;
}

/**
 * Respuesta paginada de ofertas
 */
export interface CeAcademicOfferPaginatedResponse {
  data: CeAcademicOffer[];
  total: number;
  total_pages: number;
}

/**
 * Payload para crear una oferta de educación continua
 */
export interface CreateCeAcademicOfferPayload {
  studyPlanId: number;
  campusId: number;
  quota?: number;
  completionDaysLimit?: number;
  requirements?: AcademicOfferRequirement[];
  finantials?: AcademicOfferFinantialConfig[];
}

/**
 * Payload para actualizar una oferta de educación continua
 */
export interface UpdateCeAcademicOfferPayload {
  firstInstallmentRuleId?: number | null;
  quota?: number;
  completionDaysLimit?: number | null;
  requirements?: AcademicOfferRequirement[];
  finantials?: AcademicOfferFinantialConfig[];
}

/**
 * Enum de pasos para la creación de oferta académica de EC
 */
export enum CeAcademicOfferStepEnum {
  Offer = 'offer',
  Sections = 'sections',
  Schedule = 'schedule',
  Summary = 'summary',
}

/**
 * Respuesta del endpoint de paso actual
 */
export interface CeAcademicOfferStepResponse {
  id: number;
  ceAcademicOfferId: number;
  currentStep: CeAcademicOfferStepEnum;
  createdAt: string;
  updatedAt: string;
}

/**
 * Datos de sección reducidos para el calendario agregado de una oferta de EC.
 * Se usa en el paso 3 (Horario) del wizard para mostrar los horarios
 * de todas las secciones de la oferta en un solo calendario.
 */
export interface AcademicOfferScheduleSection {
  id: number;
  name: string;
  course: { id: number; name: string };
  sectionSchedules: SectionSchedule[];
}

/**
 * Respuesta del endpoint de horarios agregados de una oferta CE.
 */
export interface CeAcademicOfferScheduleResponse {
  modalityDeliveryMode: string | null;
  sections: AcademicOfferScheduleSection[];
  timeModules: {
    id: number;
    name: string;
    shortening: string;
    startTime: string;
    endTime: string;
  }[];
  activities: { id: number; name: string }[];
}

/**
 * Fila de la tabla de secciones en el resumen
 */
export interface SummarySectionRow {
  subjectName: string;
  sectionName: string;
  modality: string;
}

/**
 * Respuesta del endpoint de resumen de una oferta CE.
 */
export interface CeAcademicOfferSummaryResponse {
  offer: CeAcademicOffer;
  sections: SummarySectionRow[];
  ceProcesses: TuitionProcess[];
}

/**
 * Payload para publicar una oferta de educación continua
 */
export interface PublishCeAcademicOfferPayload {
  tuitionProcessId: number;
  year: number;
  classStartDate?: string;
  classEndDate?: string;
  enrollmentStartDate?: string;
  enrollmentEndDate?: string;
  offerLink?: string;
  noEnrollmentRequired?: boolean;
  completionDaysLimit?: number;
}

/**
 * Payload para actualizar datos de resumen de una oferta activa
 */
export interface UpdateCeAcademicOfferSummaryPayload {
  year: number;
  classStartDate?: string;
  classEndDate?: string;
  enrollmentStartDate?: string;
  enrollmentEndDate?: string;
  offerLink?: string;
  noEnrollmentRequired?: boolean;
  completionDaysLimit?: number;
}

/**
 * Tipo genérico para opciones de Select
 */
export interface SelectOptionType {
  label: string;
  value: number;
  data?: any;
}
